/*
 * NETWORK STATE COMPONENT
 * This component is meant to replace existing component, `components/OfflineNotice/index.ts`
 *
 * Note:
 * - `OfflineNotice` will only return the HTML Element, while `OfflineNoticeState` will include the network listener and display the HTML Element as necessary
 * - `OfflineNoticeState` is backward compatible with `OfflineNotice`
 * - `OfflineNoticeState` is customizeable, eg. you can customize the title, description, as well as what the button will do
 */
import React from 'react'

import { OfflineNotice } from 'components/OfflineNotice'
import GeneralState from './GeneralNoticeState'

import NoConnectionIcon from 'assets/icons/img-no-connection.svg'

const OfflineNoticeState = ({
  // Content
  variant = 2,
  title = 'Tidak ada koneksi internet',
  description = 'Pastikan koneksi WiFi atau kuota internet Anda aktif, lalu coba lagi.',
  children,

  // Container
  noticeContainerClassNames = '',

  // Button
  showButton = false,
  buttonLabel = 'Coba Lagi',
  buttonDisabled = false,
  onButtonClick = () => {},

  //
  onStatusChange = () => {},
}: {
  variant?: 1 | 2
  title?: string
  description?: string
  children?: JSX.Element

  // Container
  noticeContainerClassNames?: string

  // Button
  showButton?: boolean
  buttonLabel?: string
  buttonDisabled?: boolean
  onButtonClick?: any

  //
  onStatusChange?: (arg0: boolean) => void
}): JSX.Element => {
  const [isOnline, setIsOnline] = React.useState<boolean>(
    window?.navigator?.onLine || false
  )

  React.useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
    }
    const handleOffline = () => {
      setIsOnline(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  React.useEffect(() => {
    onStatusChange(isOnline)
  }, [isOnline, onStatusChange])

  // Old view
  if (!isOnline && variant === 1) return <OfflineNotice />

  // Latest view
  if (!isOnline && variant === 2)
    return (
      <GeneralState
        title={title}
        description={description}
        containerClassNames={noticeContainerClassNames}
        showButton={showButton}
        buttonLabel={buttonLabel}
        buttonDisabled={buttonDisabled}
        onButtonClick={onButtonClick}
        icon={<NoConnectionIcon width={120} height={120} />}
      />
    )

  return children || null
}

OfflineNoticeState.displayName = 'OfflineNoticeState'

export default OfflineNoticeState
